import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { Field } from '../../ui/Field';
import { SelectControl } from '../../ui/TextControl';
import { ChoiceChip, ConsentCheckbox } from '../../ui/ChoiceChip';
import { asDraft } from '../../../types/registration';
import { YEAR_OPTIONS } from '../../../data/registration';
import { DECADES } from '../../../data/community';
import type { QuestionProps } from './types';

/**
 * Era first, dates second.
 *
 * Asking for a start and an end year makes everyone with a long, loose
 * connection — supporters especially — set the end date to today, which buries
 * the years that actually mattered to them. So the primary answer is one or
 * more decades, non-contiguous if that's the truth, with exact years offered
 * underneath for anyone who knows them.
 */
export function YearsQuestion({
  data,
  update
}: QuestionProps) {
  const draft = asDraft(data);
  const [exact, setExact] = useState(Boolean(draft.startYear));
  function toggleDecade(decade: string) {
    const next = draft.decades.includes(decade) ? draft.decades.filter(item => item !== decade) : [...draft.decades, decade];
    update({
      decades: next
    });
  }
  return <div className="flex flex-col gap-5" data-id="element-476" data-mp="1liw0bvl4ybxp">
      <fieldset className="flex flex-col gap-2.5" data-id="element-477" data-mp="1lhudkturc105">
        <legend className="text-small text-ink-muted" data-id="element-478" data-mp="1qirul3416p02">
          Pick every decade you were around. They don&apos;t have to run together.
        </legend>
        <div className="flex flex-wrap gap-2" data-id="element-479" data-mp="1vf8tmscrqnrx">
          {DECADES.map(decade => <ChoiceChip key={decade} checked={draft.decades.includes(decade)} onChange={() => toggleDecade(decade)} data-id="element-480" data-mp="1zh3slq788p2j">
              {decade}
            </ChoiceChip>)}
        </div>
      </fieldset>

      <ConsentCheckbox id="stillInvolved" checked={draft.stillInvolved} onChange={checked => update({
      stillInvolved: checked
    })} data-id="element-481" data-mp="2e8m5vg8pc5i7">
        I&apos;m still involved today
      </ConsentCheckbox>

      {exact ? <div className="grid gap-4 border-t border-rule pt-5 sm:grid-cols-2" data-id="element-482" data-mp="1tgnx2dqsa4nf">
          <Field name="startYear" label="First year" hint="Optional." data-id="element-483" data-mp="1ol8ljsg90ku6">
            <SelectControl id="startYear" name="startYear" placeholder="Choose a year" value={draft.startYear} options={YEAR_OPTIONS} onChange={event => update({
          startYear: event.target.value
        })} data-id="element-484" data-mp="238tn3hxh6bzz" />
          </Field>

          <Field name="endYear" label="Most recent year" hint="Leave blank if it’s ongoing." data-id="element-485" data-mp="27959az5hkkdk">
            <SelectControl id="endYear" name="endYear" placeholder="Choose a year" disabled={draft.stillInvolved} value={draft.stillInvolved ? '' : draft.endYear} options={YEAR_OPTIONS} onChange={event => update({
          endYear: event.target.value
        })} data-id="element-486" data-mp="2m6ef2xvjdqot" />
          </Field>
        </div> : <button type="button" onClick={() => setExact(true)} className="inline-flex min-h-tap w-fit items-center gap-1.5 rounded-control text-small text-ink-muted underline-offset-4 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-487" data-mp="2yiq8a7j0pkgv">
          <ChevronDownIcon aria-hidden="true" className="h-4 w-4" data-id="element-488" data-mp="2fr7622wtiafw" />
          I know the exact years
        </button>}
    </div>;
}